import { useState, useEffect } from "react";
import axios from "axios";
import { formatDate, formatTimeTo12Hour, exportDetailsToPDF, exportDetailsToExcel, exportDetailsTasksToPDF, exportDetailsTasksToExcel } from "../../../utils/exportUtils";
import { hoverScale, modalButton } from "../../../components/DesignStandardize";

const AttendanceDetailsView = ({ employeeID, initialMonth, onBack }) => {
  const [detailsData, setDetailsData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [taskPopup, setTaskPopup] = useState({ isOpen: false, record: null });

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // Helper function to get status color class
  const getStatusColorClass = (status) => {
    if (status === "Late") return "border-l-4 border-red-500 bg-red-50";
    if (status === "Friday") return "border-l-4 border-purple-500 bg-purple-50";
    if (status.includes("Holiday")) return "border-l-4 border-orange-500 bg-orange-50";
    if (status.includes("Leave")) return "border-l-4 border-blue-500 bg-blue-50";
    if (status === "Present") return "border-l-4 border-green-500 bg-green-50";
    if (status === "Absent") return "border-l-4 border-gray-400 bg-gray-50";
    return "";
  };

  // Helper function to convert MM/YYYY to readable format
  const formatMonthDisplay = (monthString) => {
    if (!monthString) {
      const now = new Date();
      return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    const [mm, yyyy] = monthString.split('/');
    const date = new Date(yyyy, mm - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Helper function to generate month options
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    // Generate last 12 months
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      const value = `${mm}/${yyyy}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    
    return options;
  };

  // Helper function to sort daily breakdown records chronologically
  const sortDailyRecords = (records) => {
    if (!records || !Array.isArray(records)) return [];
    return [...records].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
  };

  // Helper function to check if a record has any tasks
  const hasTasks = (record) => {
    return record.dailyTask1 || record.dailyTask2 || record.dailyTask3 || record.dailyTask4 || record.lateTask;
  };

  // Open task popup
  const openTaskPopup = (record) => {
    setTaskPopup({ isOpen: true, record });
  };

  // Close task popup
  const closeTaskPopup = () => {
    setTaskPopup({ isOpen: false, record: null });
  };

  const fetchDetailsData = async (empID, month) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwtToken");
      const endpoint = month
        ? `${apiUrl}/summary/${empID}?month=${month}`
        : `${apiUrl}/summary/${empID}`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `${token}`,
        },
      });

      setDetailsData(response.data);
    } catch (err) {
      console.error("Error fetching details data:", err);

      if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
        localStorage.clear();
        window.location.href = "/";
      } else if (err.response?.status === 404) {
        setError("Employee not found. Please try again.");
      } else if (err.response?.status === 500) {
        setError("An error occurred while fetching data. Please try again.");
      } else {
        setError(
          "Unable to connect to server. Please check your connection."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    fetchDetailsData(employeeID, month);
  };

  // Export handlers with error handling
  const handleExportPDF = () => {
    try {
      if (!detailsData || !detailsData.dailyBreakdown) {
        alert("No data available to export.");
        return;
      }
      const displayMonth = formatMonthDisplay(selectedMonth);
      exportDetailsToPDF(detailsData, displayMonth);
    } catch (error) {
      console.error("PDF export failed:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const handleExportExcel = async () => {
    try {
      if (!detailsData || !detailsData.dailyBreakdown) {
        alert("No data available to export.");
        return;
      }
      const displayMonth = formatMonthDisplay(selectedMonth);
      await exportDetailsToExcel(detailsData, displayMonth);
    } catch (error) {
      console.error("Excel export failed:", error);
      alert("Failed to generate Excel file. Please try again.");
    }
  };

  // New export handlers for tasks
  const handleExportTasksPDF = () => {
    try {
      if (!detailsData || !detailsData.dailyBreakdown) {
        alert("No data available to export.");
        return;
      }
      const displayMonth = formatMonthDisplay(selectedMonth);
      exportDetailsTasksToPDF(detailsData, displayMonth);
    } catch (error) {
      console.error("PDF export failed:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const handleExportTasksExcel = async () => {
    try {
      if (!detailsData || !detailsData.dailyBreakdown) {
        alert("No data available to export.");
        return;
      }
      const displayMonth = formatMonthDisplay(selectedMonth);
      await exportDetailsTasksToExcel(detailsData, displayMonth);
    } catch (error) {
      console.error("Excel export failed:", error);
      alert("Failed to generate Excel file. Please try again.");
    }
  };

  useEffect(() => {
    fetchDetailsData(employeeID, selectedMonth);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">
          Loading attendance details...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <p className="text-red-500 text-lg font-semibold">{error}</p>
          <button
            onClick={() => fetchDetailsData(employeeID, selectedMonth)}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!loading && (!detailsData || !detailsData.dailyBreakdown || detailsData.dailyBreakdown.length === 0)) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">
          No attendance details available for this employee
        </p>
        <p className="text-gray-400 text-sm mt-2">
          Try selecting a different month
        </p>
        <button
          onClick={onBack}
          className={`mt-4 ${hoverScale} ${modalButton}`}
        >
          Back to Summary
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-2 sm:p-4">
      {/* Back Button and Month Filter */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <button
          onClick={onBack}
          className={`${hoverScale} ${modalButton} text-sm sm:text-base`}
        >
          ← Back to Summary
        </button>
        
        <div className="flex gap-2 items-center">
          <label className="font-medium text-sm sm:text-base">Select Month:</label>
          <select
            className="border rounded px-3 py-2 bg-white text-sm sm:text-base"
            value={selectedMonth}
            onChange={(e) => handleMonthChange(e.target.value)}
          >
            <option value="">Current Month</option>
            {generateMonthOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Employee Header */}
      {detailsData && (
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
            {detailsData.name} - {formatMonthDisplay(selectedMonth)}
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-xs sm:text-sm text-gray-600">
            <div>
              <span className="font-semibold">Designation:</span>{" "}
              {detailsData.designation}
            </div>
            <div>
              <span className="font-semibold">Joining Date:</span>{" "}
              {formatDate(detailsData.joiningDate)}
            </div>
          </div>
        </div>
      )}

      {/* Current Month Overview */}
      {detailsData && detailsData.currentMonthOverview && (
        <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 shadow-sm">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Current Month Overview</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
            <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Total Days</p>
              <p className="text-lg sm:text-xl font-bold text-gray-800">{detailsData.currentMonthOverview.totalDays}</p>
            </div>
            <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Working Days</p>
              <p className="text-lg sm:text-xl font-bold text-gray-800">{detailsData.currentMonthOverview.totalWorkingDays}</p>
            </div>
            <div className="bg-green-50 rounded p-2 sm:p-3 border border-green-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Present</p>
              <p className="text-lg sm:text-xl font-bold text-green-600">{detailsData.currentMonthOverview.totalPresent}</p>
            </div>
            <div className="bg-orange-50 rounded p-2 sm:p-3 border border-orange-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Late</p>
              <p className="text-lg sm:text-xl font-bold text-orange-600">{detailsData.currentMonthOverview.totalLate}</p>
            </div>
            <div className="bg-red-50 rounded p-2 sm:p-3 border border-red-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Absent</p>
              <p className="text-lg sm:text-xl font-bold text-red-600">{detailsData.currentMonthOverview.totalAbsent}</p>
            </div>
            <div className="bg-blue-50 rounded p-2 sm:p-3 border border-blue-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Leaves</p>
              <p className="text-lg sm:text-xl font-bold text-blue-600">{detailsData.currentMonthOverview.totalLeaves}</p>
            </div>
            <div className="bg-purple-50 rounded p-2 sm:p-3 border border-purple-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Fridays</p>
              <p className="text-lg sm:text-xl font-bold text-purple-600">{detailsData.currentMonthOverview.totalFridays}</p>
            </div>
            <div className="bg-indigo-50 rounded p-2 sm:p-3 border border-indigo-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Holidays</p>
              <p className="text-lg sm:text-xl font-bold text-indigo-600">{detailsData.currentMonthOverview.totalHolidays}</p>
            </div>
            <div className="bg-yellow-50 rounded p-2 sm:p-3 border border-yellow-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Missing Tasks</p>
              <p className="text-lg sm:text-xl font-bold text-yellow-600">{detailsData.currentMonthOverview.totalMissingTask}</p>
            </div>
            <div className="bg-gray-50 rounded p-2 sm:p-3 border border-gray-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Attendance</p>
              <p className="text-lg sm:text-xl font-bold text-gray-800">{detailsData.currentMonthOverview.totalAttendance}</p>
            </div>
          </div>
        </div>
      )}

      {/* YTD Overview */}
      {detailsData && detailsData.ytdOverview && (
        <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200 shadow-sm">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Year-to-Date Overview</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <div className="bg-white rounded p-2 sm:p-3 border border-blue-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Total Absent</p>
              <p className="text-lg sm:text-xl font-bold text-red-600">{detailsData.ytdOverview.totalAbsent}</p>
            </div>
            <div className="bg-white rounded p-2 sm:p-3 border border-blue-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Total Late</p>
              <p className="text-lg sm:text-xl font-bold text-orange-600">{detailsData.ytdOverview.totalLate}</p>
            </div>
            <div className="bg-white rounded p-2 sm:p-3 border border-blue-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Total Leaves</p>
              <p className="text-lg sm:text-xl font-bold text-blue-600">{detailsData.ytdOverview.totalLeaves}</p>
            </div>
            <div className="bg-white rounded p-2 sm:p-3 border border-blue-200">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Missing Tasks</p>
              <p className="text-lg sm:text-xl font-bold text-yellow-600">{detailsData.ytdOverview.totalMissingTask}</p>
            </div>
          </div>
        </div>
      )}

      {/* Export Buttons */}
      <div className="space-y-3">
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Export Attendance Details</h4>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleExportPDF}
              className={`${hoverScale} ${modalButton} text-sm sm:text-base`}
              disabled={!detailsData || !detailsData.dailyBreakdown}
            >
              Export to PDF
            </button>
            <button
              onClick={handleExportExcel}
              className={`${hoverScale} ${modalButton} text-sm sm:text-base`}
              disabled={!detailsData || !detailsData.dailyBreakdown}
            >
              Export to Excel
            </button>
          </div>
        </div>

        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Export Daily Tasks Report</h4>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleExportTasksPDF}
              className={`${hoverScale} ${modalButton} text-sm sm:text-base`}
              disabled={!detailsData || !detailsData.dailyBreakdown}
            >
              Export Tasks to PDF
            </button>
            <button
              onClick={handleExportTasksExcel}
              className={`${hoverScale} ${modalButton} text-sm sm:text-base`}
              disabled={!detailsData || !detailsData.dailyBreakdown}
            >
              Export Tasks to Excel
            </button>
          </div>
        </div>
      </div>

      {/* Daily Breakdown Table */}
      <div className="overflow-x-auto -mx-2 sm:mx-0">
        <table className="min-w-full bg-white border border-gray-300 shadow-sm text-xs">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 border-b whitespace-nowrap">
                Date
              </th>
              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 border-b whitespace-nowrap">
                Login Time
              </th>
              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 border-b whitespace-nowrap">
                Logout Time
              </th>
              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 border-b whitespace-nowrap">
                Check-in Location
              </th>
              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 border-b whitespace-nowrap">
                Check-out Location
              </th>
              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 border-b whitespace-nowrap">
                Late
              </th>
              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 border-b whitespace-nowrap">
                Status
              </th>
              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 border-b whitespace-nowrap">
                Missing Task
              </th>
              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 border-b whitespace-nowrap">
                Tasks
              </th>
              <th className="px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-700 border-b whitespace-nowrap">
                Map
              </th>
            </tr>
          </thead>
          <tbody>
            {detailsData && detailsData.dailyBreakdown && sortDailyRecords(detailsData.dailyBreakdown).map((record, index) => (
              <tr 
                key={index} 
                className={`hover:bg-gray-100 transition-colors ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                } ${getStatusColorClass(record.status)}`}
              >
                <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs text-gray-800 border-b whitespace-nowrap">
                  {formatDate(record.date)}
                </td>
                <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs text-gray-800 border-b whitespace-nowrap">
                  {formatTimeTo12Hour(record.loginTime)}
                </td>
                <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs text-gray-800 border-b whitespace-nowrap">
                  {formatTimeTo12Hour(record.logoutTime)}
                </td>
                <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs text-gray-800 border-b">
                  {record.locationName || "N/A"}
                </td>
                <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs text-gray-800 border-b">
                  {record.locationNameLogout || "N/A"}
                </td>
                <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs text-gray-800 border-b whitespace-nowrap">
                  {record.late ? "Yes" : "No"}
                </td>
                <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs text-gray-800 border-b whitespace-nowrap">
                  {record.status}
                </td>
                <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs text-gray-800 border-b whitespace-nowrap">
                  {record.missingTask ? "Yes" : "No"}
                </td>
                <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs text-gray-800 border-b whitespace-nowrap">
                  {hasTasks(record) ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openTaskPopup(record);
                      }}
                      className={`${hoverScale} bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-[10px]`}
                      title="View daily tasks"
                    >
                      📋 View
                    </button>
                  ) : (
                    <span className="text-gray-400">No tasks</span>
                  )}
                </td>
                <td className="px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs text-gray-800 border-b whitespace-nowrap">
                  {record.latitude && record.longitude ? (
                    <button
                      onClick={() => window.open(`https://www.google.com/maps?q=${record.latitude},${record.longitude}`, '_blank')}
                      className={`${hoverScale} bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-[10px]`}
                      title="View location on Google Maps"
                    >
                      📍 Map
                    </button>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Task Popup Modal */}
      {taskPopup.isOpen && taskPopup.record && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeTaskPopup}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-t-lg">
              <div className="flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold">
                  Daily Tasks - {formatDate(taskPopup.record.date)}
                </h3>
                <button
                  onClick={closeTaskPopup}
                  className="text-white hover:text-gray-200 text-2xl font-bold leading-none"
                  title="Close"
                >
                  ×
                </button>
              </div>
              <p className="text-sm mt-1 text-blue-100">
                Status: {taskPopup.record.status}
              </p>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 space-y-4">
              {/* Daily Tasks */}
              {(taskPopup.record.dailyTask1 || taskPopup.record.dailyTask2 || 
                taskPopup.record.dailyTask3 || taskPopup.record.dailyTask4) && (
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                    Daily Tasks
                  </h4>
                  <div className="space-y-3">
                    {taskPopup.record.dailyTask1 && (
                      <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                        <p className="text-xs font-semibold text-green-700 mb-1">Task 1</p>
                        <p className="text-sm text-gray-800">{taskPopup.record.dailyTask1}</p>
                      </div>
                    )}
                    {taskPopup.record.dailyTask2 && (
                      <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                        <p className="text-xs font-semibold text-green-700 mb-1">Task 2</p>
                        <p className="text-sm text-gray-800">{taskPopup.record.dailyTask2}</p>
                      </div>
                    )}
                    {taskPopup.record.dailyTask3 && (
                      <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                        <p className="text-xs font-semibold text-green-700 mb-1">Task 3</p>
                        <p className="text-sm text-gray-800">{taskPopup.record.dailyTask3}</p>
                      </div>
                    )}
                    {taskPopup.record.dailyTask4 && (
                      <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                        <p className="text-xs font-semibold text-green-700 mb-1">Task 4</p>
                        <p className="text-sm text-gray-800">{taskPopup.record.dailyTask4}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Late Reason */}
              {taskPopup.record.lateTask && (
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                    Late Reason
                  </h4>
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-800">{taskPopup.record.lateTask}</p>
                  </div>
                </div>
              )}

              {/* No Tasks Message */}
              {!taskPopup.record.dailyTask1 && !taskPopup.record.dailyTask2 && 
               !taskPopup.record.dailyTask3 && !taskPopup.record.dailyTask4 && 
               !taskPopup.record.lateTask && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No tasks recorded for this day</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 p-4 rounded-b-lg border-t">
              <button
                onClick={closeTaskPopup}
                className={`${hoverScale} ${modalButton} w-full sm:w-auto`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceDetailsView;
